import { useMemo } from 'react';

import { MAX_TRIAL_DAYS, SUBSCRIPTION_STATUS } from '../helpers/const';
import { SubscriptionConstruction, BannerMessage } from '../helpers/types';
import { useTranslation } from '../i18n';

type useTrialMessageProps = {
  userTrialSubscriptions?: SubscriptionConstruction[] | null;
};

const useTrialMessage = ({
  userTrialSubscriptions,
}: useTrialMessageProps): BannerMessage | null => {
  const { t } = useTranslation();

  const trialMessage = useMemo(() => {
    if (!userTrialSubscriptions) return null;

    // User with no trials
    if (userTrialSubscriptions.length === 0) return null;

    const fewestDaysTrial: SubscriptionConstruction =
      userTrialSubscriptions.reduce((prev, curr) =>
        prev.daysRemaining < curr.daysRemaining ? prev : curr,
      );

    //User with all expired trials
    if (fewestDaysTrial.status === SUBSCRIPTION_STATUS.EXPIRED) {
      return {
        text:
          userTrialSubscriptions.length === 1
            ? t('BANNER_MESSAGE_EXPIRED_SINGLE')
            : t('BANNER_MESSAGE_EXPIRED_MULTIPLE', {
                trialsToExpire: userTrialSubscriptions.filter(
                  ({ status }) => status === SUBSCRIPTION_STATUS.EXPIRED,
                ).length,
              }),
        daysLeft: 0,
      };
    }

    //User with active trials
    //Start of trial

    if (fewestDaysTrial.daysRemaining >= MAX_TRIAL_DAYS) {
      return {
        text:
          userTrialSubscriptions.length === 1
            ? t('BANNER_MESSAGE_ACTIVE_TRIAL_START_SINGLE', {
                offeringCategory: fewestDaysTrial.offeringName,
              })
            : t('BANNER_MESSAGE_ACTIVE_TRIAL_START_MULTIPLE'),
        daysLeft: fewestDaysTrial.daysRemaining,
      };
    }

    //Trial started
    return {
      text:
        userTrialSubscriptions.length === 1
          ? t('BANNER_MESSAGE_ACTIVE_TRIALS_SINGLE', {
              daysRemaining: fewestDaysTrial.daysRemaining,
              offeringCategory: fewestDaysTrial.offeringName,
            })
          : t('BANNER_MESSAGE_ACTIVE_TRIALS_MULTIPLE', {
              daysRemaining: fewestDaysTrial.daysRemaining,
              trialsToExpire: userTrialSubscriptions.filter(
                ({ daysRemaining }) =>
                  daysRemaining === fewestDaysTrial.daysRemaining,
              ).length,
            }),
      daysLeft: fewestDaysTrial.daysRemaining,
    };
  }, [t, userTrialSubscriptions]);

  return trialMessage;
};

export default useTrialMessage;
